import React from 'react';

import {useStore} from 'effector-react';
import {TFunction} from 'i18next';
import {useTranslation} from 'react-i18next';
import {generatePath, useHistory} from 'react-router-dom';

import {ReactComponent as FilterIcon} from 'assets/svg/filter2.svg';
import {ModalFirsError} from 'components/common/ModalFirsError';
import {Page} from 'components/common/Page';
import {PermissionAccess} from 'components/common/PermissionAccess';
import {CheckboxArray} from 'components/ui-kit/CheckboxArray';
import {DatePicker} from 'components/ui-kit/DatePicker';
import {DateRange} from 'components/ui-kit/DateRange';
import {Icon} from 'components/ui-kit/Icon';
import {Link} from 'components/ui-kit/Link';
import {StatusWrapper} from 'components/ui-kit/PageComponents';
import {Select} from 'components/ui-kit/Select';
import {Tooltip} from 'components/ui-kit/Tooltip';
import {DOCUMENT_STATUS_COLORS} from 'core/constants/statuses';
import {DocumentReportType, DocumentStatus} from 'core/dictionaries';
import {ROUTE} from 'core/routes';
import {isCustomsRole$} from 'models/auth';
import {listState$, showConfigBlockChanged} from 'models/documents/documents/grid/list';
import {$invoicesFilters} from 'models/documents/documents/grid/table';
import {breakpoint, colors} from 'theme';
import {
    DocumentsAccessors,
    DocumentsGridFormattedRow,
    DocumentsGridRawRow,
    GridFieldConfig,
    UserPermission,
} from 'types';
import {useMatchMedia} from 'utils/custom-hooks';
import {formatDate} from 'utils/date';
import {handleNil} from 'utils/handleNil';
import {mapArrayToI18nOptions} from 'utils/select';

import {GridsNavigation} from '../GridsNavigation';

import {Controls} from './Controls';
import {DocumentsGridList} from './List';
import {
    ControlsWrapper,
    HeaderControls,
    LocationWrapper,
    LocationWrapperTooltip,
    LocationWrapperTooltipAddress,
    LocationWrapperTooltipName,
    NavigationWrapper,
} from './styled';
import {DocumentsGridTable} from './Table';
import {TableSearch} from './Table/search';

// reportId createdAt seller/buyer status

const getConfig = (t: TFunction, showType: boolean): GridFieldConfig[] => {
    const INVOICES: string[] = [
        ROUTE.DOCUMENTS_INVOICES,
        ROUTE.DOCUMENTS_INVOICES_INCOMING,
        ROUTE.DOCUMENTS_INVOICES_OUTGOING,
    ];

    const isInvoice = INVOICES.includes(location.pathname);

    const irn = {
        id: DocumentsAccessors.irn,
        header: t('pages.documents.documents.irn'),
        isSortable: false,
        width: 260,
    };

    const id = {
        id: DocumentsAccessors.id,
        header: t('pages.documents.documents.id'),
        isSortable: false,
        width: 300,
    };

    const createdDate = {
        id: DocumentsAccessors.createdDate,
        header: t('pages.documents.documents.createdDate'),
        filter: isInvoice ? <DateRange /> : <DatePicker />,
        isSortable: true,
    };

    const type = {
        id: DocumentsAccessors.type,
        header: t('pages.documents.documents.docType'),
        filter: showType ? (
            <Select
                options={mapArrayToI18nOptions(
                    Object.keys(DocumentReportType)
                        .filter((key) => !['UPD', 'MOVING'].includes(key))
                        .map((key) => ({key, value: key})),
                    'dictionaries.documents.type',
                )}
            />
        ) : null,
        isSortable: false,
    };

    const seller = {
        id: DocumentsAccessors.seller,
        header: t('pages.documents.documents.seller'),
        filter: (
            <CheckboxArray
                searchable
                placeholder="Enter company name"
                name="seller-names"
                options={() =>
                    $invoicesFilters.getState().seller?.map((i, index) => ({
                        id: `${index}`,
                        value: i,
                        label: i.toLowerCase(),
                    })) ?? []
                }
            />
        ),
        isSortable: true,
    };

    const buyer = {
        id: DocumentsAccessors.buyer,
        header: t('pages.documents.documents.buyer'),
        filter: (
            <CheckboxArray
                searchable
                placeholder="Enter company name"
                name="buyers-name"
                options={() =>
                    $invoicesFilters.getState().buyer?.map((i, index) => ({
                        id: `${index}`,
                        value: i,
                        label: i.toLowerCase(),
                    })) ?? []
                }
            />
        ),
        isSortable: true,
    };

    const buyerLocation = {
        id: DocumentsAccessors.buyerLocation,
        header: t('pages.documents.documents.customerstorageLocation'),
        filter: (
            <CheckboxArray
                searchable={true}
                placeholder="Enter the name or address"
                name="store-locations"
                options={() =>
                    $invoicesFilters.getState()?.buyerLocation?.map((i, index) => {
                        const {storage, address} = i;
                        return {
                            id: `${index}`,
                            value: storage.toString(),
                            label: storage.toLowerCase(),
                            text: address,
                        };
                    }) ?? []
                }
            />
        ),
        isSortable: false,
    };

    const sellerLocation = {
        id: DocumentsAccessors.sellerLocation,
        header: t('pages.documents.documents.sellersStorageLocation'),
        filter: (
            <CheckboxArray
                searchable={true}
                placeholder="Enter the name or address"
                name="store-locations"
                options={() =>
                    $invoicesFilters.getState()?.sellerLocation?.map((i, index) => {
                        const {storage, address} = i;
                        return {
                            id: `${index}`,
                            value: storage.toString(),
                            label: storage.toLowerCase(),
                            text: address,
                        };
                    }) ?? []
                }
            />
        ),
        isSortable: false,
    };

    const owner = {
        id: DocumentsAccessors.owner,
        header: t('pages.documents.documents.owner'),
        isSortable: false,
    };

    const status = {
        id: DocumentsAccessors.status,
        header: t('pages.documents.documents.status'),
        filter: (
            <CheckboxArray
                name="doc-status-names"
                options={() =>
                    $invoicesFilters.getState().status?.map((i, index) => ({
                        id: `${index}`,
                        value: i,
                        label: t(`dictionaries.documents.status.${i}`),
                    })) ?? []
                }
            />
        ),
        isSortable: false,
    };

    const columnsMapping: Record<string, GridFieldConfig[]> = {
        [ROUTE.DOCUMENTS_INVOICES]: [irn, id, createdDate, buyerLocation, status],
        [ROUTE.DOCUMENTS_INVOICES_INCOMING]: [irn, id, createdDate, seller, buyerLocation, status],
        [ROUTE.DOCUMENTS_INVOICES_OUTGOING]: [irn, id, createdDate, buyer, buyerLocation, status],
        [ROUTE.DOCUMENTS_MOVING]: [id, createdDate, buyerLocation, sellerLocation, status],
        [ROUTE.DOCUMENTS_DOCUMENTS]: [id, createdDate, type, owner, status],
    };

    return columnsMapping[location.pathname] ?? columnsMapping[ROUTE.DOCUMENTS_DOCUMENTS];
};

export function convertData(
    data: DocumentsGridRawRow[] | undefined,
    t: Function,
    lastCreatedDocumentId?: string | null,
    lastCreatedInvoiceId?: string | null,
    updOnly?: boolean,
): DocumentsGridFormattedRow[] {
    if (!data) {
        return [];
    }

    const getColors = (status: DocumentStatus) => ({
        color: DOCUMENT_STATUS_COLORS[status ?? 'DEFAULT'],
    });

    const additionalStyles = `
        text-align: left;
        max-width: 250px;
        border-radius: 8px;
        opacity: 1 !important;
    `;

    const INVOICES = [
        ROUTE.DOCUMENTS_INVOICES,
        ROUTE.DOCUMENTS_INVOICES_INCOMING,
        ROUTE.DOCUMENTS_INVOICES_OUTGOING,
    ];

    return data.map((elem) => ({
        [DocumentsAccessors.irn]: handleNil(elem.irn),
        [DocumentsAccessors.id]: handleNil(elem, (value: DocumentsGridRawRow) => {
            const pathname = () => {
                if (updOnly) {
                    return generatePath(ROUTE.INVOICES_DETAIL, {id: value.reportId});
                }

                return generatePath(ROUTE.DOCUMENTS_DETAIL, {id: value.reportId});
            };
            return (
                <PermissionAccess
                    permissions={[UserPermission.DOCUMENT_DETAIL_VIEW]}
                    placeholder={value.reportId}
                >
                    <Link
                        to={{
                            pathname: updOnly
                                ? generatePath(ROUTE.INVOICES_DETAIL, {id: value.reportId})
                                : generatePath(ROUTE.DOCUMENTS_DETAIL, {id: value.reportId}),
                            state: {from: location.pathname},
                        }}
                    >
                        <span
                            style={{
                                fontWeight:
                                    value.reportId === lastCreatedDocumentId ||
                                    value.reportId === lastCreatedInvoiceId
                                        ? 'bold'
                                        : 'unset',
                            }}
                        >
                            {value.reportId}
                        </span>
                    </Link>
                </PermissionAccess>
            );
        }),
        [DocumentsAccessors.createdDate]: handleNil(elem.createdAt, (value: string) =>
            formatDate(new Date(value), 'dd.MM.yyyy HH:mm'),
        ),
        [DocumentsAccessors.seller]: handleNil(elem.seller),
        [DocumentsAccessors.buyer]: handleNil(elem.buyer),
        [DocumentsAccessors.owner]: handleNil(elem.owner),
        [DocumentsAccessors.buyerLocation]: handleNil(elem, (value) => {
            const {buyerLocation, buyerLocationAddress} = value;
            return (
                <>
                    <LocationWrapper data-tip data-for={value.reportId}>
                        {`${buyerLocation ?? '—'} ${
                            buyerLocationAddress ? '- ' + buyerLocationAddress : ''
                        }`}
                    </LocationWrapper>
                    <Tooltip
                        id={value.reportId}
                        place={'bottom'}
                        backgroundColor="white"
                        textColor="#111111"
                        additionalStyles={
                            additionalStyles + 'box-shadow: 0px 0px 6px rgba(116, 116, 116, 0.3);'
                        }
                    >
                        <LocationWrapperTooltip>
                            <LocationWrapperTooltipName>
                                {buyerLocation ?? ''}
                            </LocationWrapperTooltipName>
                            <LocationWrapperTooltipAddress>
                                {buyerLocationAddress ?? ''}
                            </LocationWrapperTooltipAddress>
                        </LocationWrapperTooltip>
                    </Tooltip>
                </>
            );
        }),
        [DocumentsAccessors.sellerLocation]: handleNil(elem, (value: DocumentsGridRawRow) => {
            const {sellerLocation} = value;
            return (
                <>
                    <LocationWrapper data-tip data-for={value.reportId + '_seller'}>
                        {sellerLocation ?? '—'}
                    </LocationWrapper>
                    <Tooltip
                        id={value.reportId + '_seller'}
                        place={'bottom'}
                        backgroundColor="white"
                        textColor="#111111"
                        additionalStyles={
                            additionalStyles + 'box-shadow: 0px 0px 6px rgba(116, 116, 116, 0.3);'
                        }
                    >
                        <LocationWrapperTooltip>
                            <LocationWrapperTooltipName>
                                {sellerLocation ?? ''}
                            </LocationWrapperTooltipName>
                        </LocationWrapperTooltip>
                    </Tooltip>
                </>
            );
        }),
        [DocumentsAccessors.type]: handleNil(elem.type, (value: DocumentReportType) =>
            t('dictionaries.documents.type.' + value),
        ),
        [DocumentsAccessors.status]: handleNil(elem.status, (value: DocumentStatus) => (
            <StatusWrapper {...getColors(value)}>
                {INVOICES.some((i) => i === location.pathname)
                    ? t(`dictionaries.documents.statusInvoice.${value}`)
                    : t(`dictionaries.documents.status.${value}`)}
            </StatusWrapper>
        )),
    }));
}
export const PageToRedirect = ({redirectTo}: Record<string, string>) => {
    const history = useHistory();
    history.push(redirectTo);

    return <></>;
};

export const DocumentsGridPage = () => {
    const {t} = useTranslation();
    const isMobile = useMatchMedia(`(max-width: ${breakpoint.phone})`);
    const {showConfigBlock} = useStore(listState$);
    const isCustomsRole = useStore(isCustomsRole$);

    const headerControls = (
        <HeaderControls>
            <ControlsWrapper>
                <TableSearch />
                {!isCustomsRole && <Controls />}
            </ControlsWrapper>
            <NavigationWrapper needMargin>
                {!isCustomsRole && <GridsNavigation />}
            </NavigationWrapper>
        </HeaderControls>
    );

    const pageHeaderControls = isMobile ? (
        <Icon
            source={FilterIcon}
            width={18}
            height={18}
            onClick={() => showConfigBlockChanged(!showConfigBlock)}
            fillColor={showConfigBlock ? colors.brandPrimary : colors.black}
        />
    ) : (
        undefined
    );

    return (
        <Page title={t('menu.Documents')} controls={pageHeaderControls}>
            <ModalFirsError />
            {!isMobile && (
                <DocumentsGridTable
                    configFunc={getConfig}
                    dataMapper={convertData}
                    headerControls={headerControls}
                />
            )}
            {isMobile && (
                <DocumentsGridList
                    configFunc={getConfig}
                    dataMapper={convertData}
                    headerControls={headerControls}
                />
            )}
        </Page>
    );
};
